import { format } from 'date-fns';

import type {
  FormAnswerValue,
  FormField,
  OnboardingAnswerEntry,
  OnboardingFormConfig,
} from '@/types';

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

export function normalizeOnboardingDateStorageValue(value: string): string {
  const match = value.match(ISO_DATE_PREFIX);
  return match?.[1] ?? value;
}

export function formatOnboardingDateAnswer(value: string): string | null {
  const datePart = normalizeOnboardingDateStorageValue(value.trim());
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;

  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return format(date, 'PPP');
}

export function isOnboardingImageUrl(value: string): boolean {
  const isUrl = value.startsWith('http://') || value.startsWith('https://');
  if (!isUrl) return false;

  return (
    /\.(jpeg|jpg|gif|png|webp|avif|svg)(\?.*)?$/i.test(value) ||
    value.includes('res.cloudinary.com')
  );
}

export type OrderedOnboardingAnswer = {
  stepId: string;
  stepTitle: string;
  field: FormField;
  question: string;
  answer: FormAnswerValue | undefined;
};

/** Infer a form field type for a stored answer that is no longer on the live form. */
export function inferLegacyOnboardingFieldType(
  answer: FormAnswerValue | undefined
): FormField['type'] {
  if (typeof answer === 'string') {
    if (isOnboardingImageUrl(answer)) return 'IMAGE';
    if (formatOnboardingDateAnswer(answer)) return 'DATE';
  }
  if (Array.isArray(answer)) return 'MULTI_SELECT';
  return 'TEXT';
}

/**
 * Live form config plus a trailing "Other" step for any stored answers whose
 * field ids are no longer on the community form (so edit UI matches profile display).
 */
export function buildOnboardingEditFormConfig(
  config: OnboardingFormConfig | null | undefined,
  answers:
    | Record<string, FormAnswerValue | OnboardingAnswerEntry | null | undefined>
    | null
    | undefined
): OnboardingFormConfig | null {
  if (!config?.steps?.length) return config ?? null;

  const knownIds = new Set<string>();
  for (const step of config.steps) {
    for (const field of step.fields || []) knownIds.add(field.id);
  }

  const legacyFields: FormField[] = [];
  for (const [fieldId, raw] of Object.entries(answers || {})) {
    if (knownIds.has(fieldId)) continue;
    const storedQuestion =
      typeof raw === 'object' && raw !== null && 'question' in raw
        ? (raw as OnboardingAnswerEntry).question
        : '';
    const answer = getFormValueFromStoredOnboarding(raw);
    legacyFields.push({
      id: fieldId,
      type: inferLegacyOnboardingFieldType(answer),
      label: (storedQuestion || fieldId).trim() || fieldId,
      options: Array.isArray(answer)
        ? answer.filter((v): v is string => typeof v === 'string')
        : [],
      required: false,
    });
  }

  if (legacyFields.length === 0) return config;

  return {
    steps: [
      ...config.steps,
      {
        id: 'legacy',
        title: 'Other',
        fields: legacyFields,
      },
    ],
  };
}

export function getOrderedOnboardingAnswers(
  config: OnboardingFormConfig | null | undefined,
  answers:
    | Record<string, FormAnswerValue | OnboardingAnswerEntry | null | undefined>
    | null
    | undefined
): OrderedOnboardingAnswer[] {
  if (!config?.steps?.length) return [];

  const ordered: OrderedOnboardingAnswer[] = [];
  const seenFieldIds = new Set<string>();

  for (const step of config.steps) {
    for (const field of step.fields || []) {
      seenFieldIds.add(field.id);
      const raw = answers?.[field.id];
      const storedQuestion =
        typeof raw === 'object' && raw !== null && 'question' in raw
          ? (raw as OnboardingAnswerEntry).question
          : '';

      ordered.push({
        stepId: step.id,
        stepTitle: step.title,
        field,
        question: field.label || storedQuestion || field.id,
        answer: normalizeOnboardingAnswerForField(field, getFormValueFromStoredOnboarding(raw)),
      });
    }
  }

  for (const [fieldId, raw] of Object.entries(answers || {})) {
    if (seenFieldIds.has(fieldId)) continue;

    const storedQuestion =
      typeof raw === 'object' && raw !== null && 'question' in raw
        ? (raw as OnboardingAnswerEntry).question
        : '';

    ordered.push({
      stepId: 'legacy',
      stepTitle: 'Other',
      field: {
        id: fieldId,
        type: 'TEXT',
        label: storedQuestion || fieldId,
        options: [],
        required: false,
      },
      question: storedQuestion || fieldId,
      answer: normalizeOnboardingAnswerForField(
        {
          id: fieldId,
          type: 'TEXT',
          label: storedQuestion || fieldId,
          options: [],
          required: false,
        },
        getFormValueFromStoredOnboarding(raw)
      ),
    });
  }

  return ordered;
}

function normalizeOnboardingAnswerForField(
  field: FormField,
  answer: FormAnswerValue | undefined
): FormAnswerValue | undefined {
  if (field.type !== 'DATE' || typeof answer !== 'string') return answer;
  return normalizeOnboardingDateStorageValue(answer);
}

export function formatOnboardingAnswerText(
  field: FormField | undefined,
  answer: FormAnswerValue | undefined
): string {
  if (answer === undefined || answer === null) return '';
  if (Array.isArray(answer)) return answer.join(', ');

  if (field?.type === 'DATE' && typeof answer === 'string') {
    return formatOnboardingDateAnswer(answer) ?? answer;
  }

  if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
  return String(answer);
}

export function isOnboardingAnswerEmpty(answer: FormAnswerValue | undefined): boolean {
  if (answer === undefined || answer === null) return true;
  if (typeof answer === 'string') return answer.trim() === '';
  if (Array.isArray(answer)) return answer.length === 0;
  return false;
}

export function sanitizeOnboardingAnswers(
  answers:
    | Record<string, FormAnswerValue | OnboardingAnswerEntry | null | undefined>
    | null
    | undefined
): Record<string, string | string[]> {
  if (!answers) return {};

  const sanitized: Record<string, string | string[]> = {};

  Object.entries(answers).forEach(([fieldId, value]) => {
    if (value === null || value === undefined) return;

    let resolved: FormAnswerValue;
    if (typeof value === 'object' && !Array.isArray(value) && 'answer' in value) {
      resolved = (value as OnboardingAnswerEntry).answer as FormAnswerValue;
    } else {
      resolved = value as FormAnswerValue;
    }

    if (
      resolved === null ||
      (typeof resolved !== 'string' &&
        typeof resolved !== 'number' &&
        typeof resolved !== 'boolean' &&
        !Array.isArray(resolved))
    ) {
      return;
    }

    if (Array.isArray(resolved)) {
      sanitized[fieldId] = resolved.filter((item): item is string => typeof item === 'string');
      return;
    }

    sanitized[fieldId] =
      typeof resolved === 'number'
        ? String(resolved)
        : typeof resolved === 'boolean'
          ? String(resolved)
          : resolved;
  });

  return sanitized;
}

export function buildRichOnboardingAnswers(
  config: OnboardingFormConfig | null | undefined,
  sanitized: Record<string, string | string[]>,
  questionFallback?: Record<string, string>
): Record<string, OnboardingAnswerEntry> {
  const labels =
    config?.steps?.reduce<Record<string, string>>((acc, step) => {
      (step.fields || []).forEach((field) => {
        acc[field.id] = field.label;
      });
      return acc;
    }, {}) ?? {};

  const out: Record<string, OnboardingAnswerEntry> = {};
  const fieldsById =
    config?.steps?.reduce<Record<string, FormField>>((acc, step) => {
      (step.fields || []).forEach((field) => {
        acc[field.id] = field;
      });
      return acc;
    }, {}) ?? {};

  for (const [id, answer] of Object.entries(sanitized)) {
    const field = fieldsById[id];
    const normalizedAnswer =
      field?.type === 'DATE' && typeof answer === 'string'
        ? normalizeOnboardingDateStorageValue(answer)
        : answer;
    out[id] = {
      question: labels[id] || questionFallback?.[id] || '',
      answer: normalizedAnswer,
    };
  }
  return out;
}

export function getFormValueFromStoredOnboarding(raw: unknown): FormAnswerValue | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === 'object' && raw !== null && 'answer' in raw) {
    const a = (raw as OnboardingAnswerEntry).answer;
    return typeof a === 'string' || Array.isArray(a) ? (a as FormAnswerValue) : undefined;
  }
  if (typeof raw === 'number' || typeof raw === 'string' || Array.isArray(raw)) {
    return raw as FormAnswerValue;
  }
  return undefined;
}
